import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  InputGroup,
  InputLeftAddon,
  Input,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { DisplayError, Button } from 'components';
import SubmissionPrompt from 'components/SubmissionPrompt';
import { useAuthContext } from 'context/AuthProvider';
import { SUCCESS_TOAST } from 'utils';

const SendReferenceFormValidation = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('An email address is required'),
});

const SendReferences = ({
  open,
  onClose,
  selected,
}: SendReferenceProps): JSX.Element => {
  const { user } = useAuthContext();
  const {
    isOpen: promptOpen,
    onOpen: openPrompt,
    onClose: closePropmt,
  } = useDisclosure();
  const toast = useToast();

  const sendReferences = async (details: SendReferenceDetails) => {
    await axios({
      baseURL: process.env.REACT_APP_FUNCTIONS_URL,
      url: '/references/sendReferences',
      method: 'POST',
      data: {
        ...details,
      },
    });
  };

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent mb={0} mt="auto" pt={4}>
        <ModalHeader
          textAlign="center"
          fontWeight={500}
          fontSize="20px"
          lineHeight="24px"
        >
          Send reference to
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex display="flex" direction="column" mb={6}>
            <Formik
              validationSchema={SendReferenceFormValidation}
              initialValues={{
                email: '',
              }}
              onSubmit={async (values, { setStatus, setSubmitting }) => {
                setStatus(null);
                try {
                  const uid = user?.uid;
                  setSubmitting(true);
                  await sendReferences({
                    userId: uid,
                    ...values,
                    ids: selected,
                  });
                  toast({
                    description: `${selected.length} reference(s) sent!`,
                    ...SUCCESS_TOAST,
                  });
                } catch (error) {
                  setStatus(error);
                } finally {
                  setSubmitting(false);
                  onClose();
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                isValid,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }: FormikProps<SendReferencesValues>) => (
                <Form
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    openPrompt();
                  }}
                >
                  <SubmissionPrompt
                    title="Send Reference(s)?"
                    description="Are you sure you want to send the references(s)?"
                    open={promptOpen}
                    onClose={closePropmt}
                    positiveTitle="Send"
                    processRequest={() => {
                      closePropmt();
                      handleSubmit();
                    }}
                  />
                  <FormControl id="email" mb={6}>
                    <InputGroup>
                      <InputLeftAddon
                        bg="#F5F5F5"
                        borderTop={0}
                        borderLeft={0}
                        borderRadius={0}
                      >
                        To:&nbsp;
                      </InputLeftAddon>
                      <Input
                        type="email"
                        variant="flushed"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        isInvalid={!!errors.email && touched.email}
                        bg="#F5F5F5"
                        focusBorderColor="custom.levoYellow"
                        errorBorderColor="crimson"
                        placeholder="you@example.com"
                      />
                    </InputGroup>
                    <ErrorMessage name="email">{DisplayError}</ErrorMessage>
                  </FormControl>
                  <Button
                    type="submit"
                    width={['100%', '80%', '60%', '50%']}
                    disabled={!isValid || isSubmitting || !touched.email}
                    isLoading={isSubmitting}
                    text={
                      isValid && touched.email
                        ? `Send ${Object.keys(selected).length} reference(s)`
                        : 'send'
                    }
                  />
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SendReferences;
